import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Highcharts from 'highcharts';
import _ from 'lodash';

import { ChartLegendLink } from 'components/StudioCharts/ChartLegendLinkContext';
import { ChartOptionsTransform } from 'contexts/ChartOptionsTransformContext';

import { isNumberElement, isTextElement } from '../compositeUtils';
import { hasSeriesVariants } from '../seriesVariants';

// A Composite's Numbers act as the legend of its charts: a Number whose
// metric is one of an unsplit chart element's series takes that series'
// colour, and hovering either highlights the other (the chart fades its other
// series, as hovering its own legend does). Once every series of a chart has
// a Number, the chart's own legend goes.

interface CompositeLegend {
  // The metrics a Number and an unsplit chart in the block share
  linkedMetricIds: string[];
  // What each linked metric's series is drawn in, as its chart reported
  seriesColors: Record<string, string>;
  highlightedMetricId: string | undefined;
  setHighlighted: (metricId: string | undefined) => void;
  reportColors: (colors: Record<string, string>) => void;
}

const CompositeLegendContext = createContext<CompositeLegend | undefined>(
  undefined,
);

const isChartElement = (element: Studio.CompositeElement): boolean =>
  !isNumberElement(element) && !isTextElement(element);

// Split charts draw a series per dimension value, not per metric. A series
// variant asks its own question of its metric (or draws it twice), so a
// Number of the plain metric can't stand for it: such charts keep their own
// legend.
const isLinkableChart = (element: Studio.CompositeElement): boolean =>
  isChartElement(element) &&
  (element.dimensions ?? []).length === 0 &&
  !hasSeriesVariants(element);

const getNumberMetricIds = (rows: Studio.CompositeRow[]): string[] =>
  _.uniq(
    rows
      .flatMap((row) => row.elements)
      .filter(isNumberElement)
      .map((element) => (element.metricIds ?? [])[0])
      .filter((metricId): metricId is string => !!metricId),
  );

export const getLinkedMetricIds = (rows: Studio.CompositeRow[]): string[] => {
  const chartMetricIds = new Set(
    rows
      .flatMap((row) => row.elements)
      .filter(isLinkableChart)
      .flatMap((element) => element.metricIds ?? []),
  );
  return getNumberMetricIds(rows).filter((id) => chartMetricIds.has(id));
};

// Whether the chart element's legend is left to the block's Numbers: it's
// unsplit and each of its metrics has one
export const isLegendShownByNumbers = ({
  element,
  rows,
}: {
  element: Studio.CompositeElement;
  rows: Studio.CompositeRow[];
}): boolean => {
  const metricIds = element.metricIds ?? [];
  if (!isLinkableChart(element) || metricIds.length === 0) {
    return false;
  }
  const numberMetricIds = getNumberMetricIds(rows);
  return metricIds.every((id) => numberMetricIds.includes(id));
};

export const CompositeLegendProvider = ({
  rows,
  children,
}: {
  rows: Studio.CompositeRow[];
  children: JSX.Element;
}) => {
  const linkedKey = getLinkedMetricIds(rows).join(',');
  const linkedMetricIds = useMemo(
    () => (linkedKey ? linkedKey.split(',') : []),
    [linkedKey],
  );
  const [seriesColors, setSeriesColors] = useState<Record<string, string>>({});
  const [highlightedMetricId, setHighlightedMetricId] = useState<
    string | undefined
  >();
  // Each chart reports its own metrics' colours, merged with the others'
  const reportColors = useCallback(
    (colors: Record<string, string>) =>
      setSeriesColors((current) => {
        const next = { ...current, ...colors };
        return _.isEqual(current, next) ? current : next;
      }),
    [],
  );
  const value = useMemo(
    (): CompositeLegend => ({
      linkedMetricIds,
      seriesColors,
      highlightedMetricId,
      setHighlighted: setHighlightedMetricId,
      reportColors,
    }),
    [highlightedMetricId, linkedMetricIds, reportColors, seriesColors],
  );
  return (
    <CompositeLegendContext.Provider value={value}>
      {children}
    </CompositeLegendContext.Provider>
  );
};

// A Number's link to its series: its colour, and whether it's highlighted.
// Undefined while it isn't linked.
export const useLegendNumber = (
  metricId: string | undefined,
):
  | {
      color: string | undefined;
      isHighlighted: boolean;
      setIsHovered: (isHovered: boolean) => void;
    }
  | undefined => {
  const legend = useContext(CompositeLegendContext);
  const setHighlighted = legend?.setHighlighted;
  const setIsHovered = useCallback(
    (isHovered: boolean) => setHighlighted?.(isHovered ? metricId : undefined),
    [metricId, setHighlighted],
  );
  if (!legend || !metricId || !legend.linkedMetricIds.includes(metricId)) {
    return undefined;
  }
  return {
    color: legend.seriesColors[metricId],
    isHighlighted: legend.highlightedMetricId === metricId,
    setIsHovered,
  };
};

type SeriesWithMetric = { metricId?: string; linkedTo?: string };

const getMetricId = (series: Highcharts.Series): string | undefined =>
  (series.userOptions as SeriesWithMetric).metricId;

// Its metric series' colours (not linked series, e.g. a forecast's)
const getSeriesColors = (chart: Highcharts.Chart): Record<string, string> =>
  (chart.series ?? []).reduce<Record<string, string>>((colors, series) => {
    const metricId = getMetricId(series);
    const { linkedTo } = series.userOptions as SeriesWithMetric;
    if (
      metricId &&
      !linkedTo &&
      !colors[metricId] &&
      typeof series.color === 'string'
    ) {
      colors[metricId] = series.color;
    }
    return colors;
  }, {});

// The chart's options with its series hover and render reported (chained
// after any handlers of its own), and its legend hidden once every metric it
// draws is linked to a Number. Charts without a linked metric are left as
// they are.
export const withLegendLink = ({
  options,
  linkedMetricIds,
  onRender,
  onHover,
}: {
  options: Highcharts.Options;
  linkedMetricIds: string[];
  onRender: (chart: Highcharts.Chart) => void;
  onHover: (metricId: string | undefined) => void;
}): Highcharts.Options => {
  const metricIds = _.uniq(
    ((options.series ?? []) as SeriesWithMetric[])
      .map((s) => s.metricId)
      .filter((id): id is string => !!id),
  );
  if (!metricIds.some((id) => linkedMetricIds.includes(id))) {
    return options;
  }
  const isLegendHidden = metricIds.every((id) => linkedMetricIds.includes(id));
  const chartEvents = options.chart?.events ?? {};
  const seriesOptions = options.plotOptions?.series ?? {};
  const seriesEvents = seriesOptions.events ?? {};

  return {
    ...options,
    chart: {
      ...options.chart,
      events: {
        ...chartEvents,
        render(this: Highcharts.Chart, event: Event) {
          chartEvents.render?.call(this, event);
          onRender(this);
        },
      },
    },
    plotOptions: {
      ...options.plotOptions,
      series: {
        ...seriesOptions,
        events: {
          ...seriesEvents,
          mouseOver(this: Highcharts.Series, event: PointerEvent) {
            seriesEvents.mouseOver?.call(this, event);
            const metricId = getMetricId(this);
            if (metricId && linkedMetricIds.includes(metricId)) {
              onHover(metricId);
            }
          },
          mouseOut(this: Highcharts.Series, event: PointerEvent) {
            seriesEvents.mouseOut?.call(this, event);
            onHover(undefined);
          },
        },
      },
    },
    ...(isLegendHidden
      ? { legend: { ...options.legend, enabled: false } }
      : {}),
  };
};

// Fades every series but the highlighted metric's (including its forecast
// and comparison, which carry its metric id), or restores them all
const applyHighlight = ({
  chart,
  metricId,
}: {
  chart: Highcharts.Chart;
  metricId: string | undefined;
}) => {
  (chart.series ?? []).forEach((series) => {
    if (!series.visible) {
      return;
    }
    if (!metricId) {
      series.setState('');
    } else {
      series.setState(getMetricId(series) === metricId ? 'hover' : 'inactive');
    }
  });
};

// A chart element's side of the link: the transform its chart applies, and
// the highlight from a hovered Number drawn onto it. Undefined outside a
// Composite and for split charts.
export const useLegendChartTransform = (
  element: Studio.CompositeElement,
): ChartOptionsTransform | undefined => {
  const legend = useContext(CompositeLegendContext);
  const chartRef = useRef<Highcharts.Chart | undefined>();
  // Kept in refs so the transform (and the chart's options) stay the same
  // while the highlight and colours change
  const reportColorsRef = useRef(legend?.reportColors);
  reportColorsRef.current = legend?.reportColors;
  const setHighlightedRef = useRef(legend?.setHighlighted);
  setHighlightedRef.current = legend?.setHighlighted;

  const isLinkable = !!legend && isLinkableChart(element);
  const linkedKey = isLinkable ? legend.linkedMetricIds.join(',') : '';

  const transform = useMemo((): ChartOptionsTransform | undefined => {
    if (!linkedKey) {
      return undefined;
    }
    const linkedMetricIds = linkedKey.split(',');
    return (options) =>
      withLegendLink({
        options,
        linkedMetricIds,
        onRender: (chart) => {
          chartRef.current = chart;
          reportColorsRef.current?.(getSeriesColors(chart));
        },
        onHover: (metricId) => setHighlightedRef.current?.(metricId),
      });
  }, [linkedKey]);

  const highlightedMetricId = legend?.highlightedMetricId;
  useEffect(() => {
    const chart = chartRef.current;
    // Gone once the chart is destroyed (e.g. its element changed type)
    if (!transform || !chart || !chart.series) {
      return;
    }
    applyHighlight({ chart, metricId: highlightedMetricId });
  }, [highlightedMetricId, transform]);

  return transform;
};

// The same link for a chart that isn't drawn with Highcharts: it reports
// its colours and hovers itself, and draws the highlight it's handed.
// Undefined outside a Composite, for split charts, and while nothing links.
export const useLegendChartLink = (
  element: Studio.CompositeElement,
): ChartLegendLink | undefined => {
  const legend = useContext(CompositeLegendContext);
  const isLinkable = !!legend && isLinkableChart(element);
  const linkedKey = isLinkable ? legend.linkedMetricIds.join(',') : '';
  const setHighlighted = legend?.setHighlighted;
  const reportColors = legend?.reportColors;
  const highlightedMetricId = legend?.highlightedMetricId;

  return useMemo((): ChartLegendLink | undefined => {
    if (!linkedKey || !setHighlighted || !reportColors) {
      return undefined;
    }
    const linkedMetricIds = linkedKey.split(',');
    return {
      linkedMetricIds,
      highlightedMetricId,
      onHover: (metricId) =>
        setHighlighted(
          metricId && linkedMetricIds.includes(metricId) ? metricId : undefined,
        ),
      reportColors,
    };
  }, [highlightedMetricId, linkedKey, reportColors, setHighlighted]);
};
